import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import tw from "twin.macro";
import "styled-components/macro";
import { IconButton, MenuItem, Chip, InputAdornment } from "@mui/material";
import {
  MadieDialog,
  MadieSpinner,
  TruncateText,
  Pagination,
  Select,
  TextField,
} from "@madie/madie-design-system/dist/react";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import * as _ from "lodash";
import "../../../../../../measureLanding/MeasureLanding.scss";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";
import {
  ExpandIcon,
  CollapseIcon,
} from "../../../../../../../icons/MeasureListTableRightArrowIcons";
import { customSort } from "../Hooks/UseTestCases";
import { Measure, TestCase } from "@madie/madie-models";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import useMeasureServiceApi from "../../../../../../../api/useMeasureServiceApi";
import "./tcPagination.scss";
import useTestCaseServiceApi from "../../../../api/useTestCaseServiceApi";
import Typography from "@mui/material/Typography";
import { useFeatureFlags } from "@madie/madie-util";

const TH = tw.th`p-3 text-left text-sm font-bold capitalize`;

export const filterByOptions = ["Measure", "Version", "CMS ID"];
const filterMap = {
  Measure: "measureName",
  Version: "version",
  "CMS ID": "cmsId",
};

const CopyTestCaseDialog = ({ open, onClose, measure, selectedTestCases }) => {
  const measureSearchApi = useRef(useMeasureServiceApi());
  const testCaseServiceApi = useRef(useTestCaseServiceApi());
  const abortController = useRef(null);

  // utilities for pagination
  const [limit, setLimit] = useState(5);
  const [page, setPage] = useState(0);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [selectedRowId, setSelectedRowId] = React.useState<string | null>(null);
  const [hoveredHeader, setHoveredHeader] = useState<string>("");
  const [totalPages, setTotalPages] = useState<number>(0);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [visibleItems, setVisibleItems] = useState<number>(0);
  // utilities for filter & search
  const [filterBy, setFilterBy] = useState<string>("");
  const [searchField, setSearchField] = useState<string>("");
  const [selectedIdForExpansion, setSelectedIdForExpansion] = useState(null);
  const [isRowExpanded, setIsRowExpanded] = useState<boolean>(false);
  const [expandedSectionData, setExpandedSectionData] = useState<TCRow[]>([]);
  const featureFlags = useFeatureFlags();

  const [finalSearchAndFilterby, setFinalSearchAndFilterby] = useState({
    finalSearchField: "",
    finalFilterBy: "",
  });

  type TCRow = {
    id: string;
    measureName: string;
    version: string;
    actions: Measure;
    hasAssociatedMeasures: boolean;
    cmsId?: string;
  };

  const transFormData = (measureList): TCRow[] => {
    return measureList.map((measure) => ({
      id: measure?.id,
      measureName: measure?.measureName,
      version: measure?.version,
      actions: measure,
      hasAssociatedMeasures: measure?.hasAssociatedMeasures,
    }));
  };

  const handleSearch = (e) => {
    setSearchField(e.target.value);
  };
  const handleFilter = (e) => {
    setFilterBy(e.target.value);
  };
  // measures owned or shared for the current user excluding the current measure
  const [measureList, setMeasureList] = useState<Measure[]>([]);
  const [offset, setOffset] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [executing, setExecuting] = useState<boolean>(false);
  const [continueDisabled, setContinueDisabled] = useState<boolean>(
    _.isEmpty(selectedRowId)
  );
  const measureServiceApi = useRef(useMeasureServiceApi()).current;

  const fetchMeasures = useCallback(() => {
    if (!measure || !measure.model || !measure.id || !open) {
      return;
    }
    setLoading(true);
    setSelectedRowId(null);
    abortController.current = new AbortController();
    const { finalSearchField, finalFilterBy } = finalSearchAndFilterby;
    const optionalSearchProperties = [];
    if (finalFilterBy) {
      optionalSearchProperties.push(filterMap[finalFilterBy]);
    }
    // We have a condition when we first load the table where we don't want to apply the filters.
    // We want this to still fire, so we only want to append all possible filters for when "-" is selected in filters, if a searchValue is also provided
    if (!finalFilterBy && finalSearchField) {
      // apply all conditions
      filterByOptions.forEach((condition) => {
        optionalSearchProperties.push(filterMap[condition]);
      });
    }

    const searchCriteria: any = {
      searchField: finalSearchField,
      model: measure.model,
      excludeByMeasureIds: [measure.id],
      optionalSearchProperties,
    };

    if (!featureFlags?.EditTestsOnVersionedMeasures) {
      searchCriteria.draft = true;
    }
    measureSearchApi.current
      .searchMeasuresByCriteria(
        true,
        limit,
        page,
        "lastModifiedAt",
        "DESC",
        searchCriteria,
        abortController.current,
        // TODO Remove parameter when either measureSearch or EditTestsOnVersionedMeasure is removed.
        "testCase"
      )
      .then((response) => {
        const {
          content,
          totalPages,
          totalElements,
          numberOfElements,
          pageable,
        } = response;
        setTotalPages(totalPages);
        setTotalItems(totalElements);
        setVisibleItems(numberOfElements);
        setMeasureList(content);
        setOffset(pageable?.offset);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
        if (error.name !== "AbortError") {
          console.error("Failed to fetch measures:", error);
        }
      });
    // usually we'd attach the filter conditions as url params, but I don't think it makes sense if it's part of a dialog.
    // may be a smarter way to do this using a non controlled component, but it's not apparent to me now
  }, [
    measure,
    limit,
    page,
    finalSearchAndFilterby,
    filterMap,
    abortController,
    open,
  ]);

  useEffect(() => {
    fetchMeasures();
    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, [fetchMeasures, measure?.id]);

  const handleRowClick = async (actions) => {
    if (!isRowExpanded || selectedIdForExpansion !== actions?.measureSetId) {
      setSelectedIdForExpansion(actions?.measureSetId);
      const results = await measureServiceApi.getMeasuresByMeasureSetId(
        actions?.measureSetId,
        true
      );
      const filteredResults = results.filter(
        (result) => result.id !== actions?.id && result.id !== measure?.id
      );
      setIsRowExpanded(true);
      setExpandedSectionData(transFormData(filteredResults));
    } else {
      setIsRowExpanded(false);
      setExpandedSectionData(null);
      setSelectedIdForExpansion(null);
    }
  };

  const columns = useMemo<ColumnDef<Measure>[]>(() => {
    setContinueDisabled(_.isEmpty(selectedRowId));
    const columnDefs = [];
    columnDefs.push({
      accessorKey: "",
      id: "select",
      cell: ({ row }) => {
        return (
          <input
            type="radio"
            checked={selectedRowId === row.original.id}
            onChange={() => setSelectedRowId(row.original.id)}
          />
        );
      },
    });

    const updatedColumns = [
      ...columnDefs,
      {
        header: "Measure Name",
        cell: (info) => (
          <TruncateText
            text={info.row.original.measureName}
            maxLength={120}
            dataTestId={`measure-name-${info.row.original.id}`}
          />
        ),
        accessorKey: "measureName",
        sortingFn: (rowA, rowB) =>
          customSort(rowA.original.measureName, rowB.original.measureName),
      },
      {
        header: "Version",
        cell: (info) => (
          <>
            <TruncateText
              text={info.row.original.version}
              maxLength={20}
              dataTestId={`measure-version-${info.row.original.id}`}
            />
          </>
        ),
        accessorKey: "version",
        sortingFn: (rowA, rowB) =>
          customSort(rowA.original.version, rowB.original.version),
      },
      {
        header: "Status",
        cell: (info) => (
          <>
            {`${info.row.original.measureMetaData?.draft}` === "true" && (
              <Chip className="chip-draft" label="Draft" />
            )}
          </>
        ),
        accessorKey: "measureMetaData.draft",
      },
      {
        header: "CMS ID",
        cell: (info) => (
          <TruncateText
            text={_.toString(info.row.original?.measureSet?.cmsId)}
            maxLength={20}
            dataTestId={`measure-cmsId-${info.row.original.id}`}
          />
        ),
        accessorKey: "cmsId",
        sortingFn: (rowA, rowB) =>
          customSort(
            _.toString(rowA.original.measureSet.cmsId),
            _.toString(rowB.original.measureSet.cmsId)
          ),
      },
    ];

    if (featureFlags?.EditTestsOnVersionedMeasures) {
      updatedColumns.push({
        header: "",
        cell: (info) => {
          if (info.row.original?.hasAssociatedMeasures) {
            const handleKeyDown = (e) => {
              if (e.key === "Enter" || e.key === " ") {
                handleRowClick(info.row.original);
              }
            };
            return (
              <span
                role="button"
                tabIndex={0}
                onClick={() => {
                  handleRowClick(info.row.original);
                }}
                onKeyDown={handleKeyDown}
                style={{
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {isRowExpanded &&
                selectedIdForExpansion === info.row.original.measureSetId ? (
                  <CollapseIcon />
                ) : (
                  <ExpandIcon />
                )}
              </span>
            );
          } else {
            return <></>;
          }
        },
        accessorKey: "expandArrow",
        enableSorting: false,
      });
    }

    return updatedColumns;
  }, [
    selectedRowId,
    featureFlags?.EditTestsOnVersionedMeasures,
    selectedIdForExpansion,
    isRowExpanded,
    expandedSectionData,
  ]);

  const table = useReactTable({
    data: measureList,
    columns,
    getRowId: (row) => row.id,
    defaultColumn: {
      size: 200,
      minSize: 50,
      maxSize: 500,
    },
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const finalizeSearchCriteria = () => {
    const finalSearchAndFilter = {
      finalSearchField: searchField,
      finalFilterBy: filterBy,
    };
    setFinalSearchAndFilterby(finalSearchAndFilter);
  };

  const blankSearchCriteria = () => {
    setSearchField("");
    setFilterBy("");
    setFinalSearchAndFilterby({ finalFilterBy: "", finalSearchField: "" });
    setPage(0);
  };

  const onSubmit = async (e) => {
    if (_.isEmpty(selectedRowId)) {
      return;
    }
    setExecuting(true);
    setContinueDisabled(true);
    testCaseServiceApi.current
      .copyTestCasesToMeasure(
        measure.id,
        selectedRowId,
        selectedTestCases?.map((tc: TestCase) => tc.id)
      )
      .then((result) => {
        if (
          result?.copiedTestCases?.length ===
          selectedTestCases?.map((tc: TestCase) => tc.id).length
        ) {
          result.didClearExpectedValues
            ? onClose(
                "Test Cases successfully copied without expected values due to differing Population Criteria on target Measure.",
                "success"
              )
            : onClose("Test Cases have been successfully copied.", "success");
        } else if (result?.copiedTestCases?.length > 0) {
          result.didClearExpectedValues
            ? onClose(
                "Test Cases have been successfully copied without expected values due to differing Population Criteria on target Measure. Some Test Cases could not be copied.",
                "warning"
              )
            : onClose(
                "Test Cases have been successfully copied. Some Test Cases could not be copied.",
                "warning"
              );
        } else {
          onClose("Test Cases could not copied.", "danger");
        }
      })
      .catch((e) => {
        console.error(e);
        onClose(
          "Unable to copy Test Cases. Please contact the Help Desk.",
          "danger"
        );
      })
      .finally(() => {
        setExecuting(false);
      });
  };

  const handleDialogClose = (e) => {
    onClose();
  };

  const expandedColumns = useMemo<ColumnDef<Measure>[]>(() => {
    return columns;
  }, [columns]);

  return (
    <MadieDialog
      form
      title="Copy To"
      dialogProps={{
        onClose: handleDialogClose,
        open,
        onSubmit,
      }}
      cancelButtonProps={{
        variant: "secondary",
        cancelText: "Cancel",
        "data-testid": "copy-test-cases-cancel-button",
        disabled: executing,
      }}
      continueButtonProps={{
        variant: "cyan",
        type: "submit",
        "data-testid": "copy-test-cases-continue-button",
        continueText: "Save",
        disabled: continueDisabled,
      }}
      maxWidth={"lg"}
    >
      {!executing && (
        <div id="measure-landing" data-testid="measure-landing">
          <div id="tc-search">
            <div>
              <Select
                label="Filter By"
                id="filter-by-select"
                data-testid="filter-by-select"
                inputProps={{ "data-testid": "filter-by-select-input" }}
                placeHolder={{ name: "Filter By", value: "" }}
                SelectDisplayProps={{
                  "aria-required": "true",
                }}
                size="small"
                name="filterBy"
                value={filterBy}
                onChange={handleFilter}
                options={filterByOptions
                  ?.map((option) => {
                    return (
                      <MenuItem
                        key={option}
                        value={option}
                        data-testid={`filter-by-${option}`}
                      >
                        {option}
                      </MenuItem>
                    );
                  })
                  .concat(
                    <MenuItem key="-" value="" data-testid={`filter-by--`}>
                      -
                    </MenuItem>
                  )}
              />
            </div>
            <div>
              <TextField
                id="search"
                label="Search"
                placeholder="Search"
                inputProps={{
                  "data-testid": "test-case-list-search-input",
                }}
                data-testid="test-case-list-search"
                name="searchField"
                value={searchField}
                onChange={handleSearch}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    finalizeSearchCriteria();
                  }
                }}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment
                        position="start"
                        data-testid="test-cases-trigger-search"
                        onClick={finalizeSearchCriteria}
                        style={{ cursor: "pointer" }}
                      >
                        <SearchIcon />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment
                        data-testid="test-cases-clear-search"
                        position="end"
                        style={{ cursor: "pointer" }}
                        onClick={blankSearchCriteria}
                      >
                        <IconButton>
                          <ClearIcon />
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </div>
          </div>
          <div className="measure-table no-margin-top">
            <div className="table" style={{ overflow: "auto" }}>
              <table
                tw="min-w-full"
                data-testid="measure-list-tbl"
                className="ml-table"
                style={{
                  borderSpacing: "0 2em !important",
                  borderBottom: "1px solid rgb(140, 140, 140)",
                }}
              >
                <thead tw="bg-slate">
                  {table.getHeaderGroups().map((headerGroup) => (
                    <tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => {
                        const isHovered = hoveredHeader?.includes(header.id);
                        return (
                          <TH
                            key={header.id}
                            scope="col"
                            onClick={header.column.getToggleSortingHandler()}
                            onMouseEnter={() => setHoveredHeader(header.id)}
                            onMouseLeave={() => setHoveredHeader(null)}
                            className="header-cell"
                          >
                            {header.isPlaceholder ? null : (
                              <button
                                className={
                                  header.column.getCanSort()
                                    ? "cursor-pointer select-none header-button"
                                    : "header-button"
                                }
                                title={
                                  header.column.getCanSort()
                                    ? header.column.getNextSortingOrder() ===
                                      "asc"
                                      ? "Sort ascending"
                                      : header.column.getNextSortingOrder() ===
                                        "desc"
                                      ? "Sort descending"
                                      : "Clear sort"
                                    : undefined
                                }
                              >
                                <span className="arrowDisplay">
                                  {header.column.getCanSort() &&
                                    isHovered &&
                                    !header.column.getIsSorted() && (
                                      <UnfoldMoreIcon />
                                    )}
                                  {{
                                    asc: <KeyboardArrowUpIcon />,
                                    desc: <KeyboardArrowDownIcon />,
                                  }[header.column.getIsSorted() as string] ??
                                    null}
                                </span>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </button>
                            )}
                          </TH>
                        );
                      })}
                    </tr>
                  ))}
                </thead>
                <tbody className="table-body" style={{ padding: 20 }}>
                  {loading ? (
                    <div style={{ display: "flex", justifyContent: "center" }}>
                      <MadieSpinner style={{ height: 50, width: 50 }} />
                    </div>
                  ) : _.isEmpty(measureList) ? (
                    <tr>
                      <td colSpan={columns.length} tw="text-center p-2">
                        You don't have any other measures that you own or are
                        shared with you, belonging to the same model.
                      </td>
                    </tr>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <React.Fragment key={row.id}>
                        <tr
                          key={row.id}
                          className="ml-tr"
                          data-testid={`row-item`}
                          style={{
                            borderTop: "solid 1px #8c8c8c",
                            borderSpacing: "0 2em !important",
                          }}
                        >
                          {row.getVisibleCells().map((cell) => (
                            <td
                              key={cell.id}
                              data-testid={`measure-name-${cell.id}`}
                            >
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </td>
                          ))}
                        </tr>
                        {featureFlags?.EditTestsOnVersionedMeasures &&
                          selectedIdForExpansion ===
                            row.original.measureSetId &&
                          expandedSectionData?.map((subRow) => (
                            <tr key={subRow.id} className="expanded-row">
                              {expandedColumns.map((column: any) => (
                                <td key={column?.accessorKey || column.id}>
                                  {column.accessorKey === "cmsId"
                                    ? subRow?.actions?.measureSet?.cmsId || ""
                                    : flexRender(
                                        column.cell ?? column.accessorKey,
                                        {
                                          row: { original: subRow },
                                          getValue: () =>
                                            subRow[column.accessorKey],
                                        }
                                      )}
                                </td>
                              ))}
                            </tr>
                          ))}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
              <Pagination
                totalItems={totalItems}
                visibleItems={visibleItems}
                limitOptions={[5, 10, 25, 50]}
                offset={offset}
                page={page + 1}
                limit={limit}
                handlePageChange={(e, v) => {
                  setPage(v - 1);
                  setMeasureList([]);
                }}
                handleLimitChange={(e) => {
                  setLimit(e.target.value);
                  setMeasureList([]);
                }}
                count={totalPages}
                shape="rounded"
                hideNextButton={!(page + 1 < totalPages)}
                hidePrevButton={!(page > 0)}
              />
            </div>
          </div>
        </div>
      )}
      {executing && (
        <>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <MadieSpinner style={{ height: 50, width: 50 }} />
            <Typography color="inherit">Copying Test Cases...</Typography>
          </div>
        </>
      )}
    </MadieDialog>
  );
};

export default CopyTestCaseDialog;
